// ===== API DTOs (match backend exactly) =====

/**
 * Matches backend OrganizationDto (Address fields are @JsonUnwrapped)
 */
export interface OrganizationDto {
  id: string;
  bpnl: string;
  did: string;
  legalName: string;
  shortName: string;
  sector: string;
  legalForm: string;
  website: string;
  description: string;
  foundingDate: string;
  street: string;
  houseNumber: string;
  postalCode: string;
  city: string;
  country: string;
  commercialRegisterNumber: string;
  commercialRegisterCourt: string;
  taxIdNumber: string;
  admissionState: AdmissionState;
  activityState: ActivityState;
  createdDate: string;
}

/**
 * Matches backend OrganizationMemberDto
 */
export interface OrganizationMemberApiDto {
  id: string;
  organizationId: string;
  /** Keycloak user UUID (JWT sub). Read-only, set by server. */
  iamId?: string;
  title: string;
  firstName: string;
  lastName: string;
  position: string;
  email: string;
  phoneNumber: string;
  roles: string[];
  activityState: ActivityState;
}

/**
 * Matches backend OrganizationDetailsDto
 * Returned by GET /organizations and GET /organizations/{id}
 */
export interface OrganizationDetailsApiDto {
  organization: OrganizationDto;
  representative: OrganizationMemberApiDto;
}

/**
 * Matches backend OrganizationCommentDto
 */
export interface OrganizationCommentApiDto {
  id: string;
  type: CommentType;
  text: string;
  author: string;
  date: string;
}

/**
 * Matches backend OrganizationAdmissionReasoningDto
 */
export interface OrganizationAdmissionReasoningDto {
  reasoning: string;
}

/**
 * Matches backend OrganizationFilterDto (query params for GET /organizations)
 */
export interface OrganizationFilterDto {
  legalName?: string;
  activityState?: ActivityState;
  admissionState?: AdmissionState;
}

/**
 * Spring Data Page response format
 */
export interface SpringPageApiDto<T> {
  content: T[];
  pageable: {
    pageNumber: number;
    pageSize: number;
    sort: {
      unsorted: boolean;
      sorted: boolean;
      empty: boolean;
    };
    offset: number;
    unpaged: boolean;
    paged: boolean;
  };
  totalElements: number;
  totalPages: number;
  last: boolean;
  first: boolean;
  numberOfElements: number;
  size: number;
  number: number;
  sort: {
    unsorted: boolean;
    sorted: boolean;
    empty: boolean;
  };
  empty: boolean;
}

// ===== Frontend View Models =====

/**
 * View model for organization list items (used in tables)
 */
export interface OrganizationListItem {
  id: string;
  bpnl: string;
  legalName: string;
  shortName: string;
  admissionState: AdmissionState;
  activityState: ActivityState;
  createdDate: string;
  representative: {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
  };
}

/**
 * View model for organization details page
 */
export interface OrganizationDetails {
  id: string;
  bpnl: string;
  did: string;
  legalName: string;
  shortName: string;
  organisationAddress: {
    street: string;
    houseNumber: string;
    postalCode: string;
    city: string;
    country: string;
  };
  legalForm: string;
  description: string;
  taxIdNumber: string;
  foundingDate: string;
  commercialRegisterNumber: string;
  commercialRegisterCourt: string;
  website: string;
  sector: string;
  admissionState: AdmissionState;
  activityState: ActivityState;
  responsiblePerson: {
    title: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    position: string;
  };
}

/**
 * View model for organization member list items (used in member table)
 */
export interface OrganizationMemberListItem {
  id: string;
  organizationId: string;
  title: string;
  firstName: string;
  lastName: string;
  position: string;
  email: string;
  phoneNumber: string;
  roles: string[];
  activityState: ActivityState;
}

/**
 * View model for organization member details
 */
export interface OrganizationMemberDetails {
  id: string;
  organizationId: string;
  /** Keycloak user UUID (JWT sub). Read-only, set by server. */
  iamId?: string;
  title: string;
  firstName: string;
  lastName: string;
  position: string;
  email: string;
  phoneNumber: string;
  roles: string[];
  activityState: ActivityState;
}

/**
 * DTO for updating an organization (PUT /organizations/{id})
 * Matches backend OrganizationDto structure
 */
export interface OrganizationUpdateDto {
  id?: string;
  legalName: string | null;
  shortName: string | null;
  legalForm: string | null;
  street: string | null;
  houseNumber: string | null;
  postalCode: string | null;
  city: string | null;
  country: string | null;
  website: string | null;
  sector: string | null;
  description: string | null;
  taxIdNumber: string | null;
  commercialRegisterNumber: string | null;
  commercialRegisterCourt: string | null;
  foundingDate: Date | null;
  did?: string | null;
  admissionState?: AdmissionState;
  activityState?: ActivityState;
}

// ===== Enums =====

export enum ActivityState {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE'
}

export enum AdmissionState {
  APPROVED = 'APPROVED',
  PENDING = 'PENDING',
  REJECTED = 'REJECTED'
}

export enum CommentType {
  APPROVAL = 'APPROVAL',
  REJECTION = 'REJECTION',
  COMMENT = 'COMMENT'
}

// ===== Partner network types (reduced data) =====

/**
 * API DTO for partner list items (GET /organizations/partners)
 */
export interface PartnerListItemApiDto {
  id: string;
  bpnl: string;
  legalName: string;
  shortName: string;
  city: string;
  country: string;
  activityState: ActivityState;
}

/**
 * View model for partner list items in the partner network table
 */
export interface PartnerListItem {
  id: string;
  bpnl: string;
  legalName: string;
  shortName: string;
  city: string;
  country: string;
  activityState: ActivityState;
}

/**
 * API DTO for partner detail view (GET /organizations/partners/{id})
 */
export interface PartnerDetailApiDto {
  id: string;
  bpnl: string;
  did: string;
  legalName: string;
  shortName: string;
  sector: string;
  legalForm: string;
  website: string;
  description: string;
  taxIdNumber: string | null;
  commercialRegisterNumber: string | null;
  commercialRegisterCourt: string | null;
  activityState: ActivityState;
  street: string;
  houseNumber: string;
  postalCode: string;
  city: string;
  country: string;
  representativeName: string | null;
  representativeEmail: string | null;
}

/**
 * View model for partner detail page
 */
export interface PartnerDetail {
  id: string;
  bpnl: string;
  did: string;
  legalName: string;
  shortName: string;
  sector: string;
  legalForm: string;
  website: string;
  description: string;
  taxIdNumber: string | null;
  commercialRegisterNumber: string | null;
  commercialRegisterCourt: string | null;
  activityState: ActivityState;
  address: {
    street: string;
    houseNumber: string;
    postalCode: string;
    city: string;
    country: string;
  };
  representativeName: string | null;
  representativeEmail: string | null;
}

// ===== Shared types =====

export interface OrganizationComment {
  id: string;
  text: string;
  date: string;
  author: string;
  type?: CommentType;
}

/**
 * Query parameters for organization list API requests
 * Supports pagination, filtering, and sorting
 */
export interface PageQueryParams {
  page?: number;
  size?: number;
  sort?: string;
  legalName?: string;
  activityState?: string;
  admissionState?: string;
  [key: string]: string | number | undefined;
}

/**
 * Pagination metadata from API response
 */
export interface PaginationMetadata {
  pageNumber: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
  first: boolean;
  numberOfElements: number;
  size: number;
  number: number;
  empty: boolean;
}

/**
 * Wrapper for paginated list response
 */
export interface PaginatedList<T> {
  content: T[];
  pagination: PaginationMetadata;
  sort?: {
    unsorted: boolean;
    sorted: boolean;
    empty: boolean;
  };
}

// ===== Mock types =====

export interface MockedOrganization extends OrganizationDto {
  representative: {
    title: string;
    firstName: string;
    lastName: string;
    position: string;
    email: string;
    phoneNumber: string;
    roles: string[];
  };
  document: File | null;
}
